import { lazy, Suspense, useState } from 'react';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import type { GeoLocation } from 'components/GeoLocationComponent/buildGeoLocationFromGeoJson';
import { StylishNewCheckbox } from 'components/DesignSystems/New/StylishNewCheckbox';
import StylishNewSelect from 'components/DesignSystems/New/StylishNewSelect';
import { StylishNewToggleButtonGroup } from 'components/DesignSystems/New/StylishNewToggleButtonGroup';
import type { AudiencePreviewDto } from 'hooks/useNotification';
import {
  AreaAudience,
  describeAudienceCount,
  geometryOf,
  hasAreaSelection,
} from './audienceModel';

/**
 * The map pulls in ArcGIS, which most sends never need: picking named areas is
 * the default and the common case. Loading it only when someone chooses to draw
 * keeps the composer's cost proportional to what they are doing.
 */
const GeoLocationComponent = lazy(
  () => import('components/GeoLocationComponent/GeoLocationComponent')
);

enum AreaSource {
  DRAW = 'draw',
  PICK = 'pick',
}

type AorOption = { label: string; value: string };

type Props = {
  audience: AreaAudience;
  onChange: (audience: AreaAudience) => void;
  aorOptions: AorOption[];
  preview: AudiencePreviewDto | undefined;
  isPreviewLoading: boolean;
  /** Why this audience cannot be sent to yet, decided by the composer. */
  error: string | null;
  sendToEveryone: boolean;
  onSendToEveryoneChange: (value: boolean) => void;
};

/**
 * Optional filter on the people already chosen in the composer.
 *
 * Home areas match each member's home area of responsibility. A drawn shape
 * matches the Current Location on their profile. The two are alternatives
 * because they answer different questions.
 */
const AudienceAreaField: React.FC<Props> = ({
  audience,
  onChange,
  aorOptions,
  preview,
  isPreviewLoading,
  error,
  sendToEveryone,
  onSendToEveryoneChange,
}) => {
  const [source, setSource] = useState<AreaSource>(AreaSource.PICK);
  const [geoLocation, setGeoLocation] = useState<GeoLocation | undefined>(
    undefined
  );

  const handleGeoLocationChange = (location: GeoLocation | undefined) => {
    setGeoLocation(location);
    onChange({ ...audience, geometry: geometryOf(location) });
  };

  const handleSourceChange = (next: AreaSource) => {
    setSource(next);
    // Clearing the other source keeps the count honest. Carrying a stale
    // polygon behind the area picker would report recipients the operator can
    // no longer see.
    onChange(
      next === AreaSource.DRAW
        ? { geometry: geometryOf(geoLocation), aorItemIds: [] }
        : { geometry: null, aorItemIds: audience.aorItemIds }
    );
  };

  const selectedOptions = aorOptions.filter((option) =>
    audience.aorItemIds.includes(option.value)
  );

  const unreachable = Object.entries(
    preview?.unreachableByChannel ?? {}
  ).filter(([, count]) => count > 0);

  return (
    <div className="mb-3">
      <Label className="form-label">Filter by area</Label>
      <StylishNewToggleButtonGroup
        data={[
          { label: 'Home areas', value: AreaSource.PICK },
          { label: 'Custom location', value: AreaSource.DRAW },
        ]}
        selected={source}
        onChange={(value) => handleSourceChange(value as AreaSource)}
        onBlur={() => {}}
      />

      {source === AreaSource.PICK ? (
        <div className="tw:mt-3">
          <StylishNewSelect
            options={aorOptions}
            value={selectedOptions}
            onChange={(selected: AorOption[] | null) =>
              onChange({
                geometry: null,
                aorItemIds: (selected ?? []).map((option) => option.value),
              })
            }
            placeholder="Select home areas of responsibility"
            isMulti
            isClearable
            isSearchable
          />
          <p className="tw:mt-2 tw:mb-0 tw:text-xs tw:text-muted-foreground">
            Matches people whose home area of responsibility is one of these
            areas.
          </p>
        </div>
      ) : (
        <div className="tw:mt-3">
          <Suspense
            fallback={
              <p className="tw:mb-0 tw:text-xs tw:text-muted-foreground">
                Loading the map…
              </p>
            }
          >
            <GeoLocationComponent
              location={geoLocation}
              setLocation={handleGeoLocationChange}
            />
          </Suspense>
          <p className="tw:mt-2 tw:mb-0 tw:text-xs tw:text-muted-foreground">
            Matches people whose Current Location on their profile falls inside
            this shape.
          </p>
        </div>
      )}

      <div className="tw:mt-3">
        <StylishNewCheckbox
          label="Send to everyone in this area"
          id="sendToEveryoneInArea"
          checked={sendToEveryone && hasAreaSelection(audience)}
          disabled={!hasAreaSelection(audience)}
          onChange={(event) => onSendToEveryoneChange(event.target.checked)}
        />
      </div>

      {hasAreaSelection(audience) ? (
        <div className="tw:mt-2 tw:flex tw:flex-wrap tw:items-center tw:gap-2">
          <Badge variant={preview?.empty ? 'destructive' : 'secondary'}>
            {describeAudienceCount(
              isPreviewLoading || !preview ? null : preview.userCount
            )}
          </Badge>
          {preview?.aorNames.length ? (
            <span className="tw:text-xs tw:text-muted-foreground">
              {preview.aorNames.join(', ')}
            </span>
          ) : null}
        </div>
      ) : null}

      {/* An empty audience is the expected failure here, not an edge case:
          coverage is assigned per member, so a shape over unmapped ground has
          nobody behind it. */}
      {error ? (
        <p className="tw:mt-2 tw:mb-0 tw:text-xs tw:text-destructive">
          {error}
        </p>
      ) : null}

      {unreachable.length > 0 ? (
        <p className="tw:mt-1 tw:mb-0 tw:text-xs tw:text-muted-foreground">
          {unreachable
            .map(([channel, count]) => `${count} without ${channel}`)
            .join(', ')}
          . They will still receive this in PRATUS.
        </p>
      ) : null}
    </div>
  );
};

export default AudienceAreaField;
